import { useState } from 'react';
import { ShareIcon } from '@heroicons/react/24/outline';
import { useI18n } from '../i18n';
import { Portal } from './Portal';
import { CreateShareModal, scopeLabelFor, type ShareTarget } from '../share';

/**
 * BOUTON « PARTAGER » — ouvre la fenêtre de création d'un LIEN PUBLIC.
 *
 * ── CE BOUTON A CHANGÉ DE SENS EN v1.27.0 ─────────────────────────────────────────
 *
 * Jusqu'ici il copiait l'URL COURANTE : un lien parfait, mais qui exigeait un compte
 * du destinataire. Il crée désormais un partage anonyme (XPHOTO-31, ADR-0009).
 *
 * Garder les deux comportements côte à côte aurait produit DEUX boutons « Partager »
 * aux effets différents dans la même barre d'outils — le pire des cas pour qui doit
 * choisir. Le lien réservé aux comptes reste accessible là où il a toujours été : la
 * barre d'adresse du navigateur.
 *
 * ACTION DE LECTURE : le bouton n'est JAMAIS gardé par `canWrite`, et vit hors des
 * menus « Outils » et « Actions… » qui le sont. Partager ce qu'on a le droit de voir
 * en fait partie — l'API applique la même règle (garde de lecture), et bornera de
 * toute façon le partage au périmètre de son créateur.
 *
 * Deux apparences (`tone`), calquées sur les boutons voisins de chaque emplacement :
 *  - `dark`    : barre de la visionneuse (fond sombre), icône seule ;
 *  - `default` : barre d'outils de la galerie (fond clair), libellé court dès `sm`.
 */

/** Apparence du déclencheur, selon la barre qui l'accueille. */
export type ShareTone = 'dark' | 'default';

interface ShareButtonProps {
  /** Libellé accessible complet (`aria-label` + `title`), déjà localisé. */
  label: string;
  /** Ce que ce bouton propose de partager, déduit de son contexte. */
  target: ShareTarget;
  /** Titre proposé par défaut dans la fenêtre (nom du dossier, de la photo…). */
  defaultTitle: string;
  /** Apparence ; défaut `default` (barre d'outils claire). */
  tone?: ShareTone;
}

export function ShareButton({
  label,
  target,
  defaultTitle,
  tone = 'default',
}: ShareButtonProps): React.JSX.Element {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);

  const dark = tone === 'dark';
  // `dark` : `p-2` en mobile / `p-2.5` dès `sm`, comme TOUS les boutons de la barre de la
  // visionneuse (cf. `BAR_BUTTON_CLASS` dans `viewer/Viewer.tsx`). Celle-ci porte sept
  // boutons sous `sm` depuis l'ajout des actions d'écriture : à 44 px partout elle
  // déborderait d'un écran de 375 px.
  const triggerClass = dark
    ? 'rounded-md p-2 hover:bg-white/10 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white sm:p-2.5'
    : 'flex h-11 items-center gap-1.5 rounded-md px-3 text-sm font-medium text-muted transition-colors hover:bg-surface-2 hover:text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand motion-reduce:transition-none';

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title={label}
        aria-label={label}
        className={triggerClass}
      >
        <ShareIcon className={dark ? 'h-6 w-6' : 'h-5 w-5 shrink-0'} aria-hidden="true" />
        {/* Libellé court visible dès `sm` (icône seule en mobile), comme `SortMenu` ;
            il est inclus dans le libellé accessible complet (WCAG 2.5.3). */}
        {!dark && <span className="hidden sm:inline">{t('share.short')}</span>}
      </button>

      {/* Porté HORS de l'en-tête : celui-ci porte `backdrop-blur`, qui crée un
          contexte d'empilement et emprisonnerait la modale sous la barre. */}
      {open && (
        <Portal>
          <CreateShareModal
            target={target}
            scopeLabel={scopeLabelFor(target, t)}
            defaultTitle={defaultTitle}
            onClose={() => setOpen(false)}
          />
        </Portal>
      )}
    </>
  );
}
